import type { MenuItem, NavbarAuth, NavbarLogo } from '@/components/navbar'
import { defaultLocale, type Locale, type PlatformLocale } from '@/i18n/locales'

/**
 * The site's shared navigation, built per locale for the global shell (`AppLayout`).
 *
 * Two kinds of content meet here and are deliberately kept apart:
 *
 *   - **Copy** — section titles, the c2c link templates, account labels — lives in the
 *     translation store (`src/content/translations/ui/navigation.json`) and arrives
 *     through the injected translator, so translators own every visible string.
 *   - **Structure** — which routes each dropdown promotes, and the paths they point at
 *     — is editorial routing data, not translatable prose, so it stays here. It is
 *     keyed by locale because the promoted set genuinely differs: es-mx leads with a
 *     Mexico route and lists Mexican carriers, en-ca with Canadian routes and carriers,
 *     fr-ca with Montréal's (mirrors ui-react's `carrierData.js`).
 *
 * City names sit with the structure rather than the store for the same reason the
 * store holds the *editorial* rendering of a place: these are a hand-picked list, and
 * a route's label is its two cities plugged into the mode template.
 */

/** Minimal translator shape — keeps this module React-free and unit-testable. */
export type Translate = (key: string, values?: Record<string, string>) => string

/** A promoted c2c route: the two city names as displayed, plus its locale-less path. */
interface RouteLink {
    from: string
    to: string
    path: string
}

/** A promoted carrier: its name is a proper noun, so it is never translated. */
interface CarrierLink {
    name: string
    path: string
}

const BUS_ROUTES: Record<Locale, RouteLink[]> = {
    'en-us': [
        { from: 'Boston', to: 'NYC', path: '/bus/us-ma/boston/us-ny/new-york/' },
        { from: 'NYC', to: 'Boston', path: '/bus/us-ny/new-york/us-ma/boston/' },
        { from: 'DC', to: 'NYC', path: '/bus/us-dc/washington/us-ny/new-york/' },
        { from: 'LA', to: 'Vegas', path: '/bus/us-ca/los-angeles/us-nv/las-vegas/' },
        { from: 'Dallas', to: 'Houston', path: '/bus/us-tx/dallas/us-tx/houston/' },
        { from: 'Houston', to: 'Dallas', path: '/bus/us-tx/houston/us-tx/dallas/' },
        { from: 'Baltimore', to: 'NYC', path: '/bus/us-md/baltimore/us-ny/new-york/' },
        { from: 'NYC', to: 'Albany', path: '/bus/us-ny/new-york/us-ny/albany/' },
        { from: 'Orlando', to: 'Miami', path: '/bus/us-fl/orlando/us-fl/miami/' },
        { from: 'Philadelphia', to: 'NYC', path: '/bus/us-pa/philadelphia/us-ny/new-york/' },
    ],
    'es-mx': [
        {
            from: 'Ciudad de México',
            to: 'Tijuana',
            path: '/bus/mx-cmx/mexico-city/mx-bcn/tijuana/',
        },
        { from: 'Nueva York', to: 'Boston', path: '/bus/us-ny/new-york/us-ma/boston/' },
        { from: 'Washington DC', to: 'Nueva York', path: '/bus/us-dc/washington/us-ny/new-york/' },
        { from: 'Nueva York', to: 'Washington DC', path: '/bus/us-ny/new-york/us-dc/washington/' },
        { from: 'Filadelfia', to: 'Nueva York', path: '/bus/us-pa/philadelphia/us-ny/new-york/' },
        { from: 'Nueva York', to: 'Filadelfia', path: '/bus/us-ny/new-york/us-pa/philadelphia/' },
        { from: 'Baltimore', to: 'Nueva York', path: '/bus/us-md/baltimore/us-ny/new-york/' },
        { from: 'Nueva York', to: 'Baltimore', path: '/bus/us-ny/new-york/us-md/baltimore/' },
    ],
    'en-ca': [
        { from: 'Toronto', to: 'Montreal', path: '/bus/ca-on/toronto/ca-qc/montreal/' },
        { from: 'Montreal', to: 'Toronto', path: '/bus/ca-qc/montreal/ca-on/toronto/' },
        { from: 'Vancouver', to: 'Victoria', path: '/bus/ca-bc/vancouver/ca-bc/victoria/' },
        { from: 'Victoria', to: 'Vancouver', path: '/bus/ca-bc/victoria/ca-bc/vancouver/' },
        { from: 'Calgary', to: 'Edmonton', path: '/bus/ca-ab/calgary/ca-ab/edmonton/' },
        { from: 'Edmonton', to: 'Calgary', path: '/bus/ca-ab/edmonton/ca-ab/calgary/' },
        { from: 'Montreal', to: 'NYC', path: '/bus/ca-qc/montreal/us-ny/new-york/' },
        { from: 'Vancouver', to: 'Seattle', path: '/bus/ca-bc/vancouver/us-wa/seattle/' },
    ],
    'fr-ca': [
        { from: 'Montréal', to: 'New York', path: '/bus/ca-qc/montreal/us-ny/new-york/' },
        { from: 'New York', to: 'Montréal', path: '/bus/us-ny/new-york/ca-qc/montreal/' },
        { from: 'Montréal', to: 'Québec', path: '/bus/ca-qc/montreal/ca-qc/quebec-city/' },
        { from: 'Québec', to: 'Montréal', path: '/bus/ca-qc/quebec-city/ca-qc/montreal/' },
        { from: 'Montréal', to: 'Toronto', path: '/bus/ca-qc/montreal/ca-on/toronto/' },
        { from: 'Toronto', to: 'Montréal', path: '/bus/ca-on/toronto/ca-qc/montreal/' },
        { from: 'Montréal', to: 'Vancouver', path: '/bus/ca-qc/montreal/ca-bc/vancouver/' },
        { from: 'Vancouver', to: 'Montréal', path: '/bus/ca-bc/vancouver/ca-qc/montreal/' },
    ],
}

const TRAIN_ROUTES: Record<Locale, RouteLink[]> = {
    'en-us': [
        { from: 'Boston', to: 'NYC', path: '/train/us-ma/boston/us-ny/new-york/' },
        { from: 'NYC', to: 'Boston', path: '/train/us-ny/new-york/us-ma/boston/' },
        { from: 'DC', to: 'NYC', path: '/train/us-dc/washington/us-ny/new-york/' },
        { from: 'NYC', to: 'DC', path: '/train/us-ny/new-york/us-dc/washington/' },
        { from: 'San Diego', to: 'LA', path: '/train/us-ca/san-diego/us-ca/los-angeles/' },
        { from: 'LA', to: 'San Diego', path: '/train/us-ca/los-angeles/us-ca/san-diego/' },
        { from: 'Seattle', to: 'Vancouver', path: '/train/us-wa/seattle/ca-bc/vancouver/' },
        { from: 'Vancouver', to: 'Seattle', path: '/train/ca-bc/vancouver/us-wa/seattle/' },
        { from: 'Philadelphia', to: 'NYC', path: '/train/us-pa/philadelphia/us-ny/new-york/' },
        { from: 'NYC', to: 'Baltimore', path: '/train/us-ny/new-york/us-md/baltimore/' },
        { from: 'Newark', to: 'DC', path: '/train/us-nj/newark/us-dc/washington/' },
    ],
    'es-mx': [
        { from: 'Boston', to: 'Nueva York', path: '/train/us-ma/boston/us-ny/new-york/' },
        { from: 'Nueva York', to: 'Boston', path: '/train/us-ny/new-york/us-ma/boston/' },
        {
            from: 'Washington DC',
            to: 'Nueva York',
            path: '/train/us-dc/washington/us-ny/new-york/',
        },
        {
            from: 'Nueva York',
            to: 'Washington DC',
            path: '/train/us-ny/new-york/us-dc/washington/',
        },
        { from: 'San Diego', to: 'Los Ángeles', path: '/train/us-ca/san-diego/us-ca/los-angeles/' },
        { from: 'Los Ángeles', to: 'San Diego', path: '/train/us-ca/los-angeles/us-ca/san-diego/' },
        { from: 'Seattle', to: 'Vancouver', path: '/train/us-wa/seattle/ca-bc/vancouver/' },
        { from: 'Vancouver', to: 'Seattle', path: '/train/ca-bc/vancouver/us-wa/seattle/' },
    ],
    'en-ca': [
        { from: 'Toronto', to: 'Montreal', path: '/train/ca-on/toronto/ca-qc/montreal/' },
        { from: 'Montreal', to: 'Toronto', path: '/train/ca-qc/montreal/ca-on/toronto/' },
        { from: 'Toronto', to: 'Ottawa', path: '/train/ca-on/toronto/ca-on/ottawa/' },
        { from: 'Ottawa', to: 'Toronto', path: '/train/ca-on/ottawa/ca-on/toronto/' },
        { from: 'Montreal', to: 'Quebec City', path: '/train/ca-qc/montreal/ca-qc/quebec-city/' },
        { from: 'Quebec City', to: 'Montreal', path: '/train/ca-qc/quebec-city/ca-qc/montreal/' },
        { from: 'Toronto', to: 'Windsor', path: '/train/ca-on/toronto/ca-on/windsor/' },
        { from: 'Seattle', to: 'Vancouver', path: '/train/us-wa/seattle/ca-bc/vancouver/' },
    ],
    'fr-ca': [
        { from: 'Québec', to: 'Montréal', path: '/train/ca-qc/quebec-city/ca-qc/montreal/' },
        { from: 'Montréal', to: 'Québec', path: '/train/ca-qc/montreal/ca-qc/quebec-city/' },
        { from: 'Montréal', to: 'Toronto', path: '/train/ca-qc/montreal/ca-on/toronto/' },
        { from: 'Toronto', to: 'Montréal', path: '/train/ca-on/toronto/ca-qc/montreal/' },
        { from: 'Montréal', to: 'Vancouver', path: '/train/ca-qc/montreal/ca-bc/vancouver/' },
        { from: 'Vancouver', to: 'Montréal', path: '/train/ca-bc/vancouver/ca-qc/montreal/' },
        { from: 'Montréal', to: 'New York', path: '/train/ca-qc/montreal/us-ny/new-york/' },
        { from: 'New York', to: 'Montréal', path: '/train/us-ny/new-york/ca-qc/montreal/' },
    ],
}

/** The "all companies" entry is appended by the builder — it is copy, not a carrier. */
const CARRIERS: Record<Locale, CarrierLink[]> = {
    'en-us': [
        { name: 'Amtrak', path: '/carrier/amtrak-train-146/' },
        { name: 'Acela', path: '/carrier/acela-182/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
        { name: 'FlixBus USA', path: '/carrier/flixbus-us-1956/' },
        { name: 'Peter Pan Bus', path: '/carrier/peter-pan-bus-lines-132/' },
        { name: 'Red Coach', path: '/carrier/red-coach-148/' },
        { name: 'California Zephyr', path: '/carrier/amtrak-california-zephyr-186/' },
    ],
    'es-mx': [
        { name: 'Tornado Bus', path: '/carrier/tornado-bus-company-74/' },
        { name: 'Tufesa International', path: '/carrier/tufesa-international-123/' },
        { name: 'El Expresso', path: '/carrier/el-expreso-bus-235/' },
        { name: 'AVE Senda', path: '/carrier/ave-senda-169/' },
        { name: 'Transportes del Norte', path: '/carrier/transportes-del-norte-tdn-164/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
        { name: 'Autobuses Torres', path: '/carrier/autobuses-torres-1266/' },
    ],
    'en-ca': [
        { name: 'VIA Rail', path: '/carrier/via-rail-133/' },
        { name: 'Megabus', path: '/carrier/megabus-136/' },
        { name: 'Rider Express', path: '/carrier/rider-express-1333/' },
        { name: 'Ontario Northland', path: '/carrier/ontario-northland-1189/' },
        { name: 'Maritime Bus', path: '/carrier/maritime-bus-241/' },
        { name: 'Red Arrow', path: '/carrier/red-arrow-98/' },
        { name: 'Ebus', path: '/carrier/ebus-38/' },
    ],
    // VIA Rail links its own page rather than the `/carrier/` index: the closing
    // "all companies" entry already links the index.
    'fr-ca': [
        { name: 'VIA Rail', path: '/carrier/via-rail-133/' },
        { name: 'Ebus', path: '/carrier/ebus-38/' },
        { name: 'Red Arrow', path: '/carrier/red-arrow-98/' },
        { name: 'Maritime Bus', path: '/carrier/maritime-bus-241/' },
        { name: 'Cold Shot', path: '/carrier/cold-shot-bus-1338/' },
        { name: 'Ontario Northland', path: '/carrier/ontario-northland-1189/' },
        { name: 'Greyhound Bus', path: '/carrier/greyhound-bus-lines-139/' },
    ],
}

const CARRIER_INDEX_PATH = '/carrier/'

/**
 * The blog is a single shared surface — it carries no locale prefix. Trailing slash
 * required: `trailingSlash: true` (next.config) 308s `/blog`, and this is both the
 * menu's Blog entry and the blog masthead's brand link on every blog page.
 */
export const blogUrl = '/blog/'

/**
 * Prefix a site path with the active locale: `/carrier/` → `/es-mx/carrier/`.
 *
 * Which entries are also marked `native` follows who serves them. Nexus serves none of
 * the ticket, carrier, explore or account paths this header promotes — the edge routes
 * them to the legacy application — so each renders as a plain anchor rather than a routed
 * link that would prefetch a page this app cannot render. The c2c routes and the blog
 * are this app's and stay routed.
 */
function localePath(locale: Locale, path: string): string {
    return `/${locale}${path}`
}

/**
 * A locale's own homepage. The default locale's is the bare `/` — `/en-us/` 301s to
 * it (`src/proxy.ts`) — so this is the only shape that does not redirect. Shared with
 * the country picker (`locale-menu-config.ts`), which needs the same answer.
 *
 * Takes a `PlatformLocale`, not the narrower `Locale`: the picker asks it for the home
 * of every market the platform serves, including the ones only legacy renders. The
 * logo's own callers still pass a `Locale`, which is a subset.
 */
export function localeHomeUrl(locale: PlatformLocale): string {
    return locale === defaultLocale ? '/' : `/${locale}/`
}

/** The logo target: the locale's own home — for en-us, the bare root. */
export function buildSiteLogo(locale: Locale): NavbarLogo {
    return {
        url: localeHomeUrl(locale),
        src: '/next-static/logos/chiku-and-wanderu-horizontal.svg',
        width: 192,
        height: 44,
        alt: 'logo',
    }
}

function routeItems(locale: Locale, routes: RouteLink[], templateKey: string, t: Translate) {
    return routes.map((route) => ({
        title: t(templateKey, { from: route.from, to: route.to }),
        url: localePath(locale, route.path),
    }))
}

export function buildSiteMenu(locale: Locale, t: Translate): MenuItem[] {
    return [
        {
            title: t('buses'),
            url: localePath(locale, '/bus-tickets/'),
            native: true,
            items: routeItems(locale, BUS_ROUTES[locale], 'busRoute', t),
        },
        {
            title: t('trains'),
            url: localePath(locale, '/train-tickets/'),
            native: true,
            items: routeItems(locale, TRAIN_ROUTES[locale], 'trainRoute', t),
        },
        {
            title: t('carriers'),
            shortTitle: t('carriersShort'),
            url: localePath(locale, CARRIER_INDEX_PATH),
            native: true,
            items: [
                ...CARRIERS[locale].map((carrier) => ({
                    title: carrier.name,
                    url: localePath(locale, carrier.path),
                    native: true,
                })),
                {
                    title: t('allCarriers'),
                    url: localePath(locale, CARRIER_INDEX_PATH),
                    native: true,
                },
            ],
        },
        // Load-bearing: this must stay `/<locale>/explore/`, not the `/find-tickets/` it
        // sometimes redirects to. Explore is a live experiment route — the router serves
        // control in place and 302s only the treatment bucket at the target — so linking
        // the target forces every visitor into the treatment page and contaminates the
        // experiment. The 302 is a per-visitor bucket, not a permanent edge redirect.
        //
        // `native` is load-bearing for the same reason: a routed link prefetches this
        // address on viewport entry, reaching the bucket assignment with no page view
        // behind it — from the header of every page.
        { title: t('explore'), url: localePath(locale, '/explore/'), native: true },
        { title: t('blog'), url: blogUrl },
    ]
}

export function buildSiteAuth(locale: Locale, t: Translate): NavbarAuth {
    return {
        login: { title: t('login'), url: localePath(locale, '/account/login/'), native: true },
        signup: { title: t('signup'), url: localePath(locale, '/account/signup/'), native: true },
        myTrips: {
            title: t('myTrips'),
            url: localePath(locale, '/account/my-trips/'),
            native: true,
        },
        account: {
            title: t('account'),
            url: localePath(locale, '/account/settings/'),
            native: true,
        },
        points: { title: t('points'), url: localePath(locale, '/account/points/'), native: true },
    }
}
